import { defineStore } from 'pinia'
import { readonly, ref, computed } from 'vue'

/**
 * Pure Journal State Store
 *
 * This store is a pure state container with no business logic.
 * All async operations, API calls, and caching are handled by composables and repository.
 *
 * Responsibilities:
 * - Hold journal UI state
 * - Provide synchronous state mutations
 * - Expose readonly state to components
 *
 * Does NOT contain:
 * - Async actions
 * - API calls
 * - Repository calls
 * - Loading states
 * - Cache management
 * - Business logic
 */
export const useJournalStore = defineStore('journal', () => {
  // ===== STATE =====

  const _searchTerm = ref<string>('')

  // Selected entry UI state
  const _selectedEntryId = ref<number | null>(null)

  // UI state
  const _shouldDeleteEntry = ref<boolean>(false)
  const _isDetailsOpen = ref<boolean>(false)
  const _isFormDirty = ref<boolean>(false)

  // ===== COMPUTED =====
  const selectedEntryId = computed(() => _selectedEntryId.value)
  const isSearchActive = computed(() => _searchTerm.value.trim().length > 0)

  // ===== MUTATIONS (SYNCHRONOUS ONLY) =====

  // === Selected Entry Management ===

  /**
   * Set only the selected entry ID (without populating entry data).
   * Use when selecting from the list to avoid seeding truncated preview data.
   */
  const setSelectedEntryId = (id: number | null) => {
    _selectedEntryId.value = id
  }

  /**
   * Clear the selected entry
   */
  const clearSelectedEntry = () => {
    _selectedEntryId.value = null
  }

  /**
   * Set search term
   */
  const setSearchTerm = (term: string) => {
    _searchTerm.value = term
  }

  // === UI State ===

  /**
   * Set whether entry should be deleted
   */
  const setShouldDeleteEntry = (shouldDelete: boolean) => {
    _shouldDeleteEntry.value = shouldDelete
  }

  /**
   * Set whether details panel is open
   */
  const setIsDetailsOpen = (open: boolean) => {
    _isDetailsOpen.value = open
  }

  /**
   * Set whether the open details form has unsaved user changes.
   */
  const setIsFormDirty = (dirty: boolean) => {
    _isFormDirty.value = dirty
  }

  // === Convenience Methods ===

  /**
   * Reset all state to initial values
   */
  const reset = () => {
    _searchTerm.value = ''
    _selectedEntryId.value = null
    _shouldDeleteEntry.value = false
    _isDetailsOpen.value = false
    _isFormDirty.value = false
  }

  // ===== RETURN INTERFACE =====

  return {
    // === Readonly State ===
    searchTerm: readonly(_searchTerm),
    selectedEntryId,
    shouldDeleteEntry: readonly(_shouldDeleteEntry),
    isDetailsOpen: readonly(_isDetailsOpen),
    isFormDirty: readonly(_isFormDirty),

    // === Computed ===
    isSearchActive,

    // === Selected Entry Mutations ===
    setSelectedEntryId,
    clearSelectedEntry,

    // === Search Mutations ===
    setSearchTerm,

    // === UI State Mutations ===
    setShouldDeleteEntry,
    setIsDetailsOpen,
    setIsFormDirty,

    // === Convenience ===
    reset
  }
})
